import { useState, type ReactNode } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import { useGetManagedPackThreadGroups } from "@/payroll/pack-chat/manager-query";
import { PackChatConversation } from "@/payroll/pack-chat/pack-chat-conversation";
import { PackChatPackRail } from "@/payroll/pack-chat/pack-chat-pack-rail";
import { PackChatRefreshError } from "@/payroll/pack-chat/pack-chat-refresh-error";
import { usePackChatSection } from "@/payroll/pack-chat/pack-chat-section";
import { PackChatState } from "@/payroll/pack-chat/pack-chat-state";
import { packChatFirstThreadId } from "@/payroll/pack-chat/technician-groups";
import { isTemporaryPackChatError } from "@/payroll/pack-chat/thread-error-copy";
import { PackChatIdlePane } from "./pack-chat-idle-pane";
import { PackChatPanelSkeleton } from "./pack-chat-panel-skeleton";
import { PackChatTechnicianList } from "./pack-chat-technician-list";
import { THREAD_FILTER, type ThreadFilter } from "./pack-chat-thread-filter";
import { PackChatThreadQueue } from "./pack-chat-thread-queue";
import { PANE_COLUMNS } from "./pane-columns";

interface PackChatPanelProps {
  businessId: string | null;
  periodStart: string | undefined;
  /**
   * The run whose workbook this page is showing.
   *
   * Every pack read here uses it, rather than the run each question happens to be
   * pinned to: a thread is about a technician's pay for the PERIOD, and after a
   * rerun the pinned run's pack is a workbook nobody is working in. Pinned, two of
   * one technician's questions showed gross earnings $1,366 apart.
   */
  runId: string | null;
  /** A locked period takes the technicians' route in, so the empty state stops
      offering it. Managers keep theirs. */
  isLocked?: boolean;
}

/**
 * PackChat as columns: technicians, their questions, the conversation, the pack.
 *
 * It replaced a version that stacked a technician's questions as folds in the
 * reading pane. Giving the questions a column makes moving between two of them a
 * click rather than a fold and an unfold, and it leaves the conversation a pane
 * of its own — which is what let it become a chat, with a header and a reply box
 * that stay put while the messages scroll.
 */
export const PackChatPanel = ({
  businessId,
  periodStart,
  runId,
  isLocked = false,
}: PackChatPanelProps) => {
  // The start-form flag is driven from the page header, so it lives in the
  // section provider that spans both. Everything below is the panel's own.
  const { starting, stopStarting } = usePackChatSection();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<ThreadFilter>(THREAD_FILTER.all);
  const [selectedTechnicianId, setSelectedTechnicianId] = useState<
    string | null
  >(null);
  const [openedThreadId, setOpenedThreadId] = useState<string | null>(null);

  // One unfiltered fetch, shaped in the query layer: the pills need counts across
  // the whole period while the list needs the filtered threads collapsed into
  // technicians, and both fall out of the same cached response.
  const listQuery = useGetManagedPackThreadGroups(
    businessId,
    periodStart ?? null,
    { status: filter === THREAD_FILTER.all ? undefined : filter, search },
  );
  const counts = listQuery.data?.counts;
  const groups = listQuery.data?.groups ?? [];

  // Keep the active conversation selected when server priority changes.
  const activeTechnicianId =
    !starting &&
    groups.some((group) => group.technician.id === selectedTechnicianId)
      ? selectedTechnicianId
      : (groups[0]?.technician.id ?? null);
  const activeGroup = groups.find(
    (group) => group.technician.id === activeTechnicianId,
  );
  const activeThreads = activeGroup?.threads ?? [];
  const openThreadId = activeThreads.some(
    (thread) => thread.id === openedThreadId,
  )
    ? openedThreadId
    : (packChatFirstThreadId(activeThreads) ?? activeThreads[0]?.id ?? null);
  const openThread =
    activeThreads.find((thread) => thread.id === openThreadId) ?? null;

  // Record fallback selections before rendering children, so refreshes cannot
  // redirect a draft. Filters can still select the first remaining conversation.
  if (
    !starting &&
    activeTechnicianId &&
    openThreadId &&
    (selectedTechnicianId !== activeTechnicianId ||
      openedThreadId !== openThreadId)
  ) {
    setSelectedTechnicianId(activeTechnicianId);
    setOpenedThreadId(openThreadId);
  }
  const refreshFailed = Boolean(
    listQuery.data && isTemporaryPackChatError(listQuery.error),
  );

  // `md:h-full` so the columns inside can resolve a height against it: without
  // it they measured against an auto-height wrapper and grew to their content,
  // which is how the grid ended up twice the height of the card holding it.
  const shell = (children: ReactNode) => (
    <Stack
      gap={4}
      align="stretch"
      className="w-full min-w-0 md:h-full md:min-h-0"
    >
      {refreshFailed && (
        <PackChatRefreshError onRetry={() => void listQuery.refetch()} />
      )}
      {children}
    </Stack>
  );

  if (!periodStart) return shell(null);

  const retryingFailedLoad =
    listQuery.isFetching && !listQuery.data && listQuery.errorUpdatedAt > 0;
  if (listQuery.isLoading && !retryingFailedLoad)
    return shell(<PackChatPanelSkeleton />);

  if ((listQuery.isError || retryingFailedLoad) && !refreshFailed) {
    return shell(
      <PackChatState
        error={listQuery.error ?? retryingFailedLoad}
        retrying={listQuery.isFetching}
        title="PackChat couldn't be loaded"
        body="Try again. If it keeps failing, contact support."
        action={
          <Button
            variant="secondary"
            size="sm"
            disabled={listQuery.isFetching}
            aria-busy={listQuery.isFetching || undefined}
            className="mt-2"
            onClick={() => void listQuery.refetch()}
          >
            {listQuery.isFetching && (
              <Spinner data-icon="inline-start" aria-hidden />
            )}
            Try again
          </Button>
        }
      />,
    );
  }

  // Nothing has ever been asked in this period: four empty columns would say the
  // same thing four times.
  if (counts?.all === 0 && !starting) {
    return shell(
      <PackChatState
        layout="workspace"
        title="No questions this period"
        body={
          isLocked
            ? "This pay period is locked, so technicians can no longer raise a question."
            : "Technicians can raise a question from any figure on their PayPack."
        }
        footnote="You can start a conversation yourself from New thread above."
      />,
    );
  }

  const nothingMatches = groups.length === 0 && !starting;
  // Neither the queue nor the pack earns space over the start form or an empty
  // result.
  const reading = Boolean(!starting && !nothingMatches && activeGroup);
  // One question is not a queue. A column holding a single row repeats what the
  // conversation beside it already says, so the conversation takes the width
  // instead and the column comes back when there is a choice to make.
  const showThreads = reading && activeThreads.length > 1;
  const showPack = reading && Boolean(openThread);

  return shell(
    // Fills the card at md+, which the tab sizes against the viewport, so each
    // column scrolls on its own the way an inbox does. It has to resolve to a
    // definite height rather than a `max-height`: a percentage height resolves
    // against the parent's *specified* height, so under `max-h` a scroll viewport
    // grows to its content and gets clipped by `overflow: hidden` — rows below
    // the fold become unreachable rather than scrollable. Below md the columns
    // stack and flow with the page.
    <Stack
      gap={0}
      align="stretch"
      className={cn(
        // No gap: the columns are divided by hairlines and own their gutters, so
        // each one's scrollbar lands on a real edge.
        "grid min-w-0 md:h-full",
        {
          [PANE_COLUMNS.form]: starting,
          [PANE_COLUMNS.queue]: !starting && showThreads,
          [PANE_COLUMNS.conversation]: !starting && !showThreads && showPack,
          [PANE_COLUMNS.list]: !starting && !showThreads && !showPack,
        },
      )}
    >
      <PackChatTechnicianList
        starting={starting}
        search={search}
        onSearchChange={setSearch}
        filter={filter}
        counts={counts}
        onFilterChange={setFilter}
        groups={groups}
        activeTechnicianId={activeTechnicianId}
        onSelectTechnician={(technicianId) => {
          stopStarting();
          setSelectedTechnicianId(technicianId);
          setOpenedThreadId(null);
        }}
      />

      {showThreads && (
        <PackChatThreadQueue
          technicianName={activeGroup?.technician.name}
          threads={activeThreads}
          openThreadId={openThreadId}
          onSelectThread={setOpenedThreadId}
        />
      )}

      {activeGroup && openThread && !starting && !nothingMatches ? (
        // The conversation owns this column: its header and its reply box stay
        // put, and only the messages scroll.
        <Stack gap={0} align="stretch" className="min-h-0 min-w-0">
          <PackChatConversation
            key={openThread.id}
            businessId={businessId}
            periodStart={periodStart}
            runId={runId}
            thread={openThread}
            technicianName={activeGroup.technician.name}
            onReplied={setOpenedThreadId}
          />
        </Stack>
      ) : (
        <PackChatIdlePane
          businessId={businessId}
          periodStart={periodStart}
          runId={runId}
          starting={starting}
          nothingMatches={nothingMatches}
          search={search}
          filter={filter}
          onShowAll={() => {
            setSearch("");
            setFilter(THREAD_FILTER.all);
          }}
          onCancelStart={stopStarting}
          onStarted={(thread) => {
            setFilter(THREAD_FILTER.all);
            setSelectedTechnicianId(thread.technician.id);
            setOpenedThreadId(thread.id);
            stopStarting();
          }}
        />
      )}

      {showPack && activeGroup && openThread && (
        <Stack
          gap={0}
          align="stretch"
          className="hidden h-full min-h-0 min-w-0 border-l border-line lg:flex"
        >
          <PackChatPackRail
            businessId={businessId}
            runId={runId ?? openThread.ai_script_run_id}
            technicianId={activeGroup.technician.id}
            threads={activeThreads}
            openThreadId={openThreadId}
            onSelectThread={setOpenedThreadId}
          />
        </Stack>
      )}
    </Stack>,
  );
};
